import { Alert, Typography, useTheme } from '@themenu/design-system'
import type { FC, FormEvent, PropsWithChildren, ReactNode } from 'react'

import { useIntl } from 'react-intl'

import { BackButtonLayout } from '../../../components/Shared/BackButtonLayout'

/**
 * Shared skeleton for every BR activation step. Encodes the cross-cutting
 * conventions once (form layout, large blue-accented title, error Alert, sticky
 * bottom action area) so the per-step views only declare their fields and
 * actions — matching directory's login/profile/reset look & feel.
 */
type ActivationStepLayoutProps = PropsWithChildren<{
  /** i18n id of the title. Supports a blue `<strong>` accent. */
  titleId: string
  /** Bottom action area (primary button, secondary links). */
  footer?: ReactNode
  /** Surfaced generic error (rendered in a solid error Alert). */
  errorMessageId?: string | null
  /** Optional action slot rendered inside the error Alert (e.g. a care link). */
  errorActions?: ReactNode
  /** Extra rich-text format values merged into the error formatMessage call (e.g. a link tag). */
  errorFormatValues?: Record<string, ReactNode | ((chunks: ReactNode[]) => ReactNode)>
  errorId?: string
  showBackButton?: boolean
  onBack?: () => void
  /** When provided the wrapper is a `<form>` and this runs on submit. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
  testId?: string
  /** Vertically center the content instead of pushing the footer to the bottom. */
  centered?: boolean
}>

export const ActivationStepLayout: FC<ActivationStepLayoutProps> = ({
  titleId,
  footer,
  errorMessageId = null,
  errorActions,
  errorFormatValues,
  errorId,
  showBackButton = false,
  onBack,
  onSubmit,
  testId,
  centered = false,
  children,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const justify = centered ? 'justify-center' : 'justify-between'
  const grow = centered ? 'flex-1 xs:h-full' : 'flex-1 xs:flex-none'

  const body = (
    <>
      <div className="flex flex-col gap-24">
        <Typography variant="largeTitle" className="!block">
          {formatMessage(
            { id: titleId },
            {
              strong: (text: ReactNode) => (
                <Typography
                  variant="largeTitle"
                  component="span"
                  style={{ color: theme.colors.title.blue }}
                  display="inline"
                >
                  {text}
                </Typography>
              ),
              br: () => <br />,
            },
          )}
        </Typography>
        {errorMessageId && (
          <div data-testid="br-activation-error">
            <Alert
              severity="error"
              backgroundOpacity="solid"
              id={errorId}
              secondaryText={formatMessage({ id: errorMessageId }, { br: () => <br />, ...errorFormatValues })}
              actions={errorActions}
            />
          </div>
        )}
        <div className="flex flex-col gap-16">{children}</div>
      </div>
      {footer && <div className="flex flex-col gap-8">{footer}</div>}
    </>
  )

  return (
    <BackButtonLayout showBackButton={showBackButton} onBack={onBack}>
      {onSubmit ? (
        <form
          data-testid={testId ?? 'br-activation-form'}
          className={`flex w-full ${grow} flex-col ${justify} gap-24 px-20`}
          onSubmit={onSubmit}
        >
          {body}
        </form>
      ) : (
        <div
          data-testid={testId ?? 'br-activation-step'}
          className={`flex w-full ${grow} flex-col ${justify} gap-24 px-20`}
        >
          {body}
        </div>
      )}
    </BackButtonLayout>
  )
}
